// 0 = Open | 1 = Adopted | 2 = Closed — mirrors CommentResolutionStatus enum in autogenerated-piping.ts
export enum CommentResolutionStatus {
  Open = 0,
  Adopted = 1,
  Closed = 2
}

// Mirrors UserType enum in autogenerated-piping.ts.
// null = comment created before this field was introduced.
export enum CommentAuthorRole {
  Author = 0,            // Revision author / owner
  Approver = 1,
  InternalReviewer = 2,
  ExternalReviewer = 3
}

export class CommentViewModel {
  id: string;
  pipeClassId: string;
  revisionId: string;
  contextType: 'summary' | 'components' | 'valves' | 'branchtables';
  parentCommentId?: string; // For threading - null if it's a root comment
  authorName: string;
  authorId: string;
  // Role of the comment author within the revision. null for legacy comments.
  authorRole: CommentAuthorRole | null;
  content: string;
  createdDate: Date;
  updatedDate?: Date;
  isEdited: boolean;
  replies: CommentViewModel[]; // Child comments for threading
  level: number; // Nesting level for UI display (0 = root, 1 = first reply, etc.)
  resolutionStatus: CommentResolutionStatus; // Open/Adopted/Closed for publishing gate

  constructor(init?: Partial<CommentViewModel>) {
    this.id = init?.id || '';
    this.pipeClassId = init?.pipeClassId || '';
    this.revisionId = init?.revisionId || '';
    this.contextType = init?.contextType || 'summary';
    this.parentCommentId = init?.parentCommentId || null;
    this.authorName = init?.authorName || '';
    this.authorId = init?.authorId || '';
    this.authorRole = init?.authorRole ?? null;
    this.content = init?.content || '';
    this.createdDate = init?.createdDate || new Date();
    this.updatedDate = init?.updatedDate || null;
    this.isEdited = init?.isEdited || false;
    this.replies = init?.replies || [];
    this.level = init?.level || 0;
    this.resolutionStatus = init?.resolutionStatus ?? CommentResolutionStatus.Open;
  }

  /**
   * Adds a reply to this comment
   */
  addReply(reply: CommentViewModel): void {
    reply.parentCommentId = this.id;
    reply.level = this.level + 1;
    this.replies.push(reply);
  }

  /**
   * Gets all replies recursively (flattened)
   */
  getAllRepliesFlat(): CommentViewModel[] {
    const allReplies: CommentViewModel[] = [];

    for (const reply of this.replies) {
      allReplies.push(reply);
      allReplies.push(...reply.getAllRepliesFlat());
    }

    return allReplies;
  }

  /**
   * Gets total reply count including nested replies
   */
  getTotalReplyCount(): number {
    let count = this.replies.length;
    for (const reply of this.replies) {
      count += reply.getTotalReplyCount();
    }
    return count;
  }

  /**
   * Updates the content and marks as edited
   */
  updateContent(newContent: string): void {
    this.content = newContent;
    this.updatedDate = new Date();
    this.isEdited = true;
  }
}

export interface CommentFormData {
  content: string;
  parentCommentId?: string;
}
